import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, CircleDot, Link2, Minus, Plus, RotateCcw } from "lucide-react";
import { registerDeckAudio } from "../../Utils/djAudioController";
import { subscribeTicker } from "../../Utils/djTicker";
import { SectionLabel, CornerButton, Knob } from "../../Components/Hardware";
import { BEATS_PER_BAR, fmt, reportAudioError } from "./utils";

const TEMPO_MIN = 0.8;
const TEMPO_MAX = 1.2;
const TEMPO_RANGE = TEMPO_MAX - TEMPO_MIN;
const rateToKnob = (r) => Math.max(0, Math.min(1, (r - TEMPO_MIN) / TEMPO_RANGE));
const knobToRate = (k) => TEMPO_MIN + k * TEMPO_RANGE;

function BeatLed({ deck, audioRef }) {
  const ledRef = useRef(null);
  const stateRef = useRef({
    nextBeatIdx: 0,
    lastMs: 0,
    intensity: 0,
    lastTick: performance.now(),
  });

  useEffect(() => {
    stateRef.current.nextBeatIdx = 0;
    stateRef.current.lastMs = 0;
    stateRef.current.intensity = 0;
  }, [deck.track?.mediaId, deck.analysis?.beatGrid]);

  useEffect(() => {
    const tick = () => {
      const led = ledRef.current;
      if (!led) return;
      const audio = audioRef.current;
      const beats = deck.analysis?.beatGrid;
      const s = stateRef.current;
      const now = performance.now();
      const dt = now - s.lastTick;
      s.lastTick = now;

      s.intensity = Math.max(0, s.intensity - dt / 140);

      if (audio && !audio.paused && beats && beats.length) {
        const ms = audio.currentTime * 1000;
        if (ms + 50 < s.lastMs) {
          let lo = 0;
          let hi = beats.length;
          while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (beats[mid] <= ms) lo = mid + 1;
            else hi = mid;
          }
          s.nextBeatIdx = lo;
        }
        while (s.nextBeatIdx < beats.length && beats[s.nextBeatIdx] <= ms) {
          const isDown = s.nextBeatIdx % BEATS_PER_BAR === 0;
          s.intensity = isDown ? 1 : 0.45;
          s.nextBeatIdx += 1;
        }
        s.lastMs = ms;
      }

      const I = s.intensity;
      led.style.opacity = String(0.18 + 0.82 * I);
      led.style.background = I > 0.7 ? "#4ade80" : "#16a34a";
      led.style.boxShadow =
        I > 0.05 ? `0 0 ${3 + 14 * I}px rgba(34,197,94,${0.35 + 0.6 * I})` : "none";
    };
    return subscribeTicker(tick);
  }, [audioRef, deck.track?.mediaId, deck.analysis?.beatGrid]);

  return (
    <div
      ref={ledRef}
      className="w-2 h-2 rounded-full shrink-0"
      style={{ background: "#16a34a", opacity: 0.18 }}
      aria-hidden
    />
  );
}

function Tonearm({ progress, isPlaying, accent }) {
  const PIVOT_X = 5;
  const PIVOT_Y = 5;
  const ARM_LEN = 64;
  const COUNTERWEIGHT_LEN = 14;
  const THETA_REST = -18;
  const THETA_PLAY_START = -1;
  const THETA_PLAY_END = 45;
  const HEADSHELL_OFFSET_DEG = 22;
  const HEADSHELL_LEN = 5;

  const playAngle = THETA_PLAY_START + (THETA_PLAY_END - THETA_PLAY_START) * progress;
  const liveTarget = isPlaying ? playAngle : THETA_REST;

  const [animating, setAnimating] = useState(false);
  const [frozenTarget, setFrozenTarget] = useState(liveTarget);
  const prevIsPlayingRef = useRef(isPlaying);
  if (prevIsPlayingRef.current !== isPlaying) {
    prevIsPlayingRef.current = isPlaying;
    setAnimating(true);
    setFrozenTarget(liveTarget);
  }
  useEffect(() => {
    if (!animating) return;
    const t = setTimeout(() => setAnimating(false), 1500);
    return () => clearTimeout(t);
  }, [animating]);

  const targetAngle = animating ? frozenTarget : liveTarget;

  const armEndX = PIVOT_X + ARM_LEN;
  const cwEndX = PIVOT_X - COUNTERWEIGHT_LEN;
  const headOffX = HEADSHELL_LEN * Math.cos((HEADSHELL_OFFSET_DEG * Math.PI) / 180);
  const headOffY = HEADSHELL_LEN * Math.sin((HEADSHELL_OFFSET_DEG * Math.PI) / 180);
  const headTipX = armEndX + headOffX;
  const headTipY = PIVOT_Y + headOffY;
  const needleColor = accent === "blue" ? "#60a5fa" : "#f472b6";

  return (
    <svg
      className="absolute inset-0 pointer-events-none z-20"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ overflow: "visible" }}
    >
      <defs>
        <linearGradient
          id={`arm-${accent}`}
          gradientUnits="userSpaceOnUse"
          x1={PIVOT_X}
          y1={PIVOT_Y}
          x2={armEndX}
          y2={PIVOT_Y}
        >
          <stop offset="0%" stopColor="#71717a" />
          <stop offset="50%" stopColor="#e4e4e7" />
          <stop offset="100%" stopColor="#a1a1aa" />
        </linearGradient>
        <radialGradient id={`pivot-${accent}`} cx="0.35" cy="0.35">
          <stop offset="0%" stopColor="#a1a1aa" />
          <stop offset="60%" stopColor="#3f3f46" />
          <stop offset="100%" stopColor="#18181b" />
        </radialGradient>
      </defs>

      <g
        style={{
          transformBox: "view-box",
          transformOrigin: `${PIVOT_X}px ${PIVOT_Y}px`,
          transform: `rotate(${targetAngle}deg)`,
          transition: animating ? "transform 1.4s cubic-bezier(0.65, 0, 0.35, 1)" : "none",
        }}
      >
        <line
          x1={PIVOT_X}
          y1={PIVOT_Y}
          x2={cwEndX}
          y2={PIVOT_Y}
          stroke="#52525b"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <circle cx={cwEndX} cy={PIVOT_Y} r="3" fill="#27272a" stroke="#71717a" strokeWidth="0.4" />

        <line
          x1={PIVOT_X}
          y1={PIVOT_Y}
          x2={armEndX}
          y2={PIVOT_Y}
          stroke={`url(#arm-${accent})`}
          strokeWidth="1.6"
          strokeLinecap="round"
        />

        <line
          x1={armEndX}
          y1={PIVOT_Y}
          x2={headTipX}
          y2={headTipY}
          stroke="#3f3f46"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
        <line
          x1={armEndX}
          y1={PIVOT_Y}
          x2={headTipX}
          y2={headTipY}
          stroke="#a1a1aa"
          strokeWidth="0.6"
        />

        <circle cx={headTipX} cy={headTipY} r="1.1" fill={needleColor}>
          {isPlaying && (
            <animate attributeName="opacity" values="1;0.6;1" dur="1.4s" repeatCount="indefinite" />
          )}
        </circle>
      </g>

      <circle cx={PIVOT_X} cy={PIVOT_Y} r="4" fill={`url(#pivot-${accent})`} stroke="#52525b" strokeWidth="0.5" />
      <circle cx={PIVOT_X} cy={PIVOT_Y} r="1" fill="#d4d4d8" />
    </svg>
  );
}

function Disc({ deck, accent, audioRef, onPlayPause, onCue, onSync }) {
  const discRef = useRef(null);
  const containerRef = useRef(null);
  const angleRef = useRef(0);
  const lastTsRef = useRef(null);
  const scratchRef = useRef(null);
  const ringColor = accent === "blue" ? "#3b82f6" : "#ec4899";

  useEffect(() => {
    let raf;
    const step = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = ts - lastTsRef.current;
      lastTsRef.current = ts;
      if (deck.isPlaying && !scratchRef.current) {
        angleRef.current = (angleRef.current + (dt * 0.06)) % 360;
        if (discRef.current) {
          discRef.current.style.transform = `rotate(${angleRef.current}deg)`;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [deck.isPlaying]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const pointerAngleAt = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      return Math.atan2(clientY - cy, clientX - cx);
    };

    const onMove = (e) => {
      const s = scratchRef.current;
      if (!s) return;
      const point = e.touches ? e.touches[0] : e;
      const angle = pointerAngleAt(point.clientX, point.clientY);
      let delta = angle - s.lastPointerAngle;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      s.cumulativeRad += delta;
      s.lastPointerAngle = angle;

      const deltaDeg = (s.cumulativeRad * 180) / Math.PI;
      const newAngle = ((s.startDiscAngle + deltaDeg) % 360 + 360) % 360;
      angleRef.current = newAngle;
      if (discRef.current) {
        discRef.current.style.transform = `rotate(${newAngle}deg)`;
      }

      const audio = audioRef?.current;
      if (audio && audio.duration) {
        const deltaSec = deltaDeg / 180;
        const newTime = s.startAudioTime + deltaSec;
        audio.currentTime = Math.max(0, Math.min(audio.duration, newTime));
      }
    };

    const onUp = () => {
      scratchRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };

    const onDown = (e) => {
      const audio = audioRef?.current;
      if (!audio || !deck.track) return;
      e.preventDefault();
      const point = e.touches ? e.touches[0] : e;
      scratchRef.current = {
        lastPointerAngle: pointerAngleAt(point.clientX, point.clientY),
        cumulativeRad: 0,
        startDiscAngle: angleRef.current,
        startAudioTime: audio.currentTime,
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onUp);
    };

    el.addEventListener("mousedown", onDown);
    el.addEventListener("touchstart", onDown, { passive: false });
    return () => {
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("touchstart", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
  }, [audioRef, deck.track]);

  const art = deck.track?.albumArtUrl;
  const accentBg = accent === "blue" ? "bg-blue-600 hover:bg-blue-500" : "bg-pink-600 hover:bg-pink-500";
  const durationMs = (deck.track?.duration || 0) * 1000;
  const progress = durationMs > 0 ? Math.max(0, Math.min(1, deck.currentTimeMs / durationMs)) : 0;
  return (
    <div className="flex items-center justify-center w-full h-full">
      <div
        className="relative"
        style={{
          width: "min(58vh, 88%, 300px)",
          aspectRatio: "1 / 1",
        }}
      >
        <div
          ref={containerRef}
          className="absolute inset-0 rounded-full select-none"
          style={{
            boxShadow: `0 0 0 6px #0a0a0a, 0 0 0 8px ${ringColor}55`,
            background: "#0a0a0a",
            cursor: deck.track ? "grab" : "default",
            touchAction: "none",
          }}
        >
          <div
            ref={discRef}
            className="absolute inset-2 rounded-full bg-neutral-800 bg-cover bg-center pointer-events-none"
            style={{
              backgroundImage: art ? `url(${art})` : undefined,
              transition: "none",
            }}
          />
          <div
            className="absolute left-1/2 top-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
            style={{ background: ringColor }}
          />
        </div>

        {deck.track && <Tonearm progress={progress} isPlaying={deck.isPlaying} accent={accent} />}

        <CornerButton
          position="top-right"
          onClick={onSync}
          disabled={!deck.analysis}
          tooltip="Sync — phase-align beats with the other deck"
        >
          <Link2 className="w-4 h-4" />
        </CornerButton>
        <CornerButton
          position="bottom-left"
          onClick={onCue}
          disabled={!deck.analysis}
          tooltip="Cue — jump to the drop (intro end)"
        >
          <CircleDot className="w-4 h-4" />
        </CornerButton>
        <CornerButton
          position="bottom-right"
          onClick={onPlayPause}
          disabled={!deck.track}
          tooltip={deck.isPlaying ? "Pause" : "Play"}
          large
          className={deck.isPlaying ? "bg-white text-black hover:bg-neutral-200" : `${accentBg} text-white`}
        >
          {deck.isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
        </CornerButton>
      </div>
    </div>
  );
}

function drawTimeline(canvas, deck, currentMs, accent) {
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const cssW = rect.width;
  const cssH = rect.height;
  if (cssW === 0 || cssH === 0) return;
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const bgGrad = ctx.createLinearGradient(0, 0, 0, cssH);
  bgGrad.addColorStop(0, "#111113");
  bgGrad.addColorStop(1, "#050506");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, cssW, cssH);

  const durationMs = (deck.track?.duration || 0) * 1000;
  if (!durationMs) return;
  const xForMs = (ms) => (ms / durationMs) * cssW;

  const analysis = deck.analysis;
  if (analysis?.introEndMs != null) {
    ctx.fillStyle = "rgba(34, 197, 94, 0.16)";
    ctx.fillRect(0, 0, xForMs(analysis.introEndMs), cssH);
  }
  if (analysis?.outroStartMs != null) {
    ctx.fillStyle = "rgba(244, 63, 94, 0.16)";
    const x = xForMs(analysis.outroStartMs);
    ctx.fillRect(x, 0, cssW - x, cssH);
  }

  if (analysis?.beatGrid) {
    const MIN_PX = 6;
    let lastDown = -Infinity;
    let lastAny = -Infinity;
    for (let i = 0; i < analysis.beatGrid.length; i++) {
      const x = xForMs(analysis.beatGrid[i]);
      const isDownbeat = i % BEATS_PER_BAR === 0;
      if (isDownbeat) {
        if (x - lastDown < MIN_PX) continue;
        lastDown = x;
        ctx.strokeStyle = "rgba(239, 68, 68, 0.55)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, cssH * 0.72);
        ctx.lineTo(x, cssH);
        ctx.stroke();
      } else {
        if (x - lastAny < MIN_PX) continue;
        lastAny = x;
        ctx.strokeStyle = "rgba(239, 68, 68, 0.22)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, cssH * 0.85);
        ctx.lineTo(x, cssH);
        ctx.stroke();
      }
    }
  }

  const elapsedColor =
    accent === "blue" ? "rgba(59, 130, 246, 0.20)" : "rgba(236, 72, 153, 0.20)";
  const playheadX = Math.max(0, Math.min(cssW, xForMs(currentMs)));
  ctx.fillStyle = elapsedColor;
  ctx.fillRect(0, 0, playheadX, cssH);

  const playheadColor = accent === "blue" ? "#60a5fa" : "#f472b6";
  ctx.strokeStyle = playheadColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(playheadX, 0);
  ctx.lineTo(playheadX, cssH);
  ctx.stroke();

  ctx.fillStyle = playheadColor;
  ctx.beginPath();
  ctx.arc(playheadX, 5, 3.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(playheadX, cssH - 5, 3.5, 0, Math.PI * 2);
  ctx.fill();
}

function DeckTimeline({ deck, accent, audioRef }) {
  const canvasRef = useRef(null);
  const deckRef = useRef(deck);
  deckRef.current = deck;

  useEffect(() => {
    let raf;
    const step = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const audio = audioRef?.current;
        const currentMs =
          audio && !Number.isNaN(audio.currentTime) ? audio.currentTime * 1000 : 0;
        drawTimeline(canvas, deckRef.current, currentMs, accent);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [accent, audioRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dragStateRef = { current: false };

    const seekFromClientX = (clientX) => {
      const currentDeck = deckRef.current;
      const audio = audioRef?.current;
      const durationMs = (currentDeck.track?.duration || 0) * 1000;
      if (!audio || !durationMs) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      audio.currentTime = (ratio * durationMs) / 1000;
    };

    const onMove = (e) => {
      if (!dragStateRef.current) return;
      const p = e.touches ? e.touches[0] : e;
      seekFromClientX(p.clientX);
    };

    const onUp = () => {
      dragStateRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };

    const onDown = (e) => {
      if (!deckRef.current.track) return;
      e.preventDefault();
      dragStateRef.current = true;
      const p = e.touches ? e.touches[0] : e;
      seekFromClientX(p.clientX);
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onUp);
    };

    canvas.addEventListener("mousedown", onDown);
    canvas.addEventListener("touchstart", onDown, { passive: false });
    return () => {
      canvas.removeEventListener("mousedown", onDown);
      canvas.removeEventListener("touchstart", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
  }, [audioRef]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{
        cursor: deck.track ? "pointer" : "default",
        touchAction: "none",
      }}
    />
  );
}

function TempoStrip({ deck, onUserRate, accent, side }) {
  const effectiveRate = deck.effectiveRate ?? 1;
  const userRate = deck.userRate ?? 1;
  const orchestratorDriving = Math.abs(effectiveRate - userRate) > 0.0005;
  const nativeBpm = deck.analysis?.bpm;
  const effectiveBpm = nativeBpm ? nativeBpm * effectiveRate : null;
  const pct = (effectiveRate - 1) * 100;
  const pctStr = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
  const disabled = !deck.track;
  const pctColor = accent === "blue" ? "text-blue-300" : "text-pink-300";
  const knobColor = accent === "blue" ? "#60a5fa" : "#f472b6";
  const knobGlow = accent === "blue" ? "#3b82f6" : "#ec4899";
  const knobOnInside = side === "A" ? "right" : "left";

  const handleKnobChange = (k) => {
    const next = Math.round(knobToRate(k) * 1000) / 1000;
    onUserRate(next);
  };

  const controls = (
    <div className="flex flex-col flex-1 min-w-0 gap-1.5">
      <div className={`flex items-center gap-2 ${knobOnInside === "right" ? "" : "justify-end"}`}>
        <SectionLabel>Tempo</SectionLabel>
        <div className={`flex items-center gap-1 ${knobOnInside === "right" ? "" : "ml-auto"}`}>
          <button
            type="button"
            onClick={() => onUserRate(userRate - 0.005)}
            disabled={disabled}
            className="w-6 h-6 flex items-center justify-center rounded bg-neutral-800 hover:bg-neutral-700 disabled:bg-neutral-900 disabled:text-neutral-700 text-neutral-300"
            title="Tempo −0.5%"
          >
            <Minus className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onUserRate(userRate + 0.005)}
            disabled={disabled}
            className="w-6 h-6 flex items-center justify-center rounded bg-neutral-800 hover:bg-neutral-700 disabled:bg-neutral-900 disabled:text-neutral-700 text-neutral-300"
            title="Tempo +0.5%"
          >
            <Plus className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onUserRate(1)}
            disabled={disabled || userRate === 1}
            className="w-6 h-6 flex items-center justify-center rounded bg-neutral-800 hover:bg-neutral-700 disabled:bg-neutral-900 disabled:text-neutral-700 text-neutral-300"
            title="Reset to native tempo"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div
        className={`flex flex-col font-mono leading-tight ${
          knobOnInside === "right" ? "items-start text-left" : "items-end text-right"
        }`}
      >
        <span className={`text-[10px] ${pctColor} flex items-center gap-1.5`}>
          {orchestratorDriving && !disabled && knobOnInside !== "right" && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-amber-400"
              style={{ boxShadow: "0 0 6px #fbbf24" }}
              title="Auto-DJ is driving the tempo"
            />
          )}
          <span>{pctStr}</span>
          {orchestratorDriving && !disabled && knobOnInside === "right" && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-amber-400"
              style={{ boxShadow: "0 0 6px #fbbf24" }}
              title="Auto-DJ is driving the tempo"
            />
          )}
        </span>
        {effectiveBpm != null ? (
          <span className="text-xs text-white">{effectiveBpm.toFixed(1)} BPM</span>
        ) : (
          <span className="text-xs text-neutral-500">—</span>
        )}
      </div>
    </div>
  );

  const knob = (
    <Knob
      value={rateToKnob(effectiveRate)}
      onChange={handleKnobChange}
      size={64}
      defaultValue={rateToKnob(1)}
      colorLow={knobColor}
      colorHigh={knobColor}
      glowLow={knobGlow}
      glowHigh={knobGlow}
      ariaLabel={`Tempo deck ${side}`}
      disabled={disabled}
      wheelStep={0.01}
      wheelStepFine={0.001}
    />
  );

  const insidePadClass = knobOnInside === "right" ? "pr-20" : "pl-20";

  return (
    <div
      className={`px-3 py-2 border-t border-neutral-800 bg-gradient-to-b from-neutral-950 to-black flex items-center gap-3 rounded-b-lg ${insidePadClass}`}
    >
      {knobOnInside === "left" && knob}
      {controls}
      {knobOnInside === "right" && knob}
    </div>
  );
}

export default function Deck({ side, deck, audioRef, onPlayPause, onCue, onSync, onLoadedMetadata, onTimeUpdate, onIsPlayingChange, onUserRate }) {
  const accent = side === "A" ? "blue" : "pink";
  const borderClass = side === "A" ? "border-blue-500/40" : "border-pink-500/40";

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => onTimeUpdate(audio.currentTime * 1000);
    const onPlay = () => onIsPlayingChange(true);
    const onPause = () => onIsPlayingChange(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [audioRef, onTimeUpdate, onIsPlayingChange, deck.track]);

  return (
    <div className={`flex-1 min-w-0 bg-neutral-950 border ${borderClass} rounded-lg flex flex-col`}>
      <div className="relative border-b border-neutral-800 overflow-hidden rounded-t-lg" style={{ height: "76px" }}>
        <DeckTimeline deck={deck} accent={accent} audioRef={audioRef} />
        <div
          className="absolute inset-0 px-4 py-3 flex items-center justify-between pointer-events-none z-10"
          style={{ textShadow: "0 1px 2px rgba(0,0,0,0.85)" }}
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold flex items-center gap-1.5">
              <BeatLed deck={deck} audioRef={audioRef} />
              Deck {side}
            </div>
            {deck.track ? (
              <>
                <div className="text-base font-semibold truncate text-white">{deck.track.title}</div>
                <div className="text-xs text-neutral-300 truncate">{deck.track.artist}</div>
              </>
            ) : (
              <div className="text-sm text-neutral-500 italic">Empty</div>
            )}
          </div>
          <div className="text-right text-xs text-neutral-200 shrink-0 ml-3">
            {deck.analysis?.bpm != null && (
              <div className="font-mono text-sm text-white">{deck.analysis.bpm.toFixed(1)} BPM</div>
            )}
            {(deck.analysis?.camelot || deck.analysis?.energy != null) && (
              <div className="font-mono text-[11px] text-neutral-300 flex items-center gap-2 justify-end">
                {deck.analysis?.camelot && (
                  <span className="text-emerald-300">{deck.analysis.camelot}</span>
                )}
                {deck.analysis?.energy != null && (
                  <span className="text-amber-300">E {deck.analysis.energy.toFixed(2)}</span>
                )}
              </div>
            )}
            <div className="font-mono">
              {fmt(deck.currentTimeMs)} / {fmt((deck.track?.duration || 0) * 1000)}
            </div>
            {deck.analysisStatus && deck.analysisStatus !== "done" && (
              <div className="text-amber-400">analysis: {deck.analysisStatus}</div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 p-1 min-h-0 flex items-center justify-center">
        <Disc
          deck={deck}
          accent={accent}
          audioRef={audioRef}
          onPlayPause={onPlayPause}
          onCue={onCue}
          onSync={onSync}
        />
      </div>

      <TempoStrip
        deck={deck}
        onUserRate={onUserRate}
        accent={accent}
        side={side}
      />

      {deck.track && (
        <audio
          ref={(el) => {
            audioRef.current = el;
            registerDeckAudio(side, el);
          }}
          src={deck.track.audioUrl}
          preload="auto"
          crossOrigin="anonymous"
          onLoadedMetadata={onLoadedMetadata}
          onError={(e) => reportAudioError(side, deck.track, e.target.error)}
        />
      )}
    </div>
  );
}
